<template>
  <header class="lf-header" :class="{ 'lf-header--scrolled': isScrolled, 'lf-header--open': menuOpen }">
    <div class="container">
      <div class="lf-header__inner">
        <router-link to="/" class="lf-brand" @click="closeMenu">
          <img src="/images/logo-lafuerza.png" alt="LaFuerza IT Chile" width="36" height="36" class="lf-brand__logo">
          <span class="lf-brand__name">LaFuerza<span class="lf-accent">.</span>IT</span>
        </router-link>

        <button class="lf-burger" :aria-expanded="menuOpen" aria-controls="lf-nav" aria-label="Menú" @click="toggleMenu">
          <span></span><span></span>
        </button>

        <nav id="lf-nav" class="lf-nav" :class="{ 'lf-nav--open': menuOpen }">
          <ul>
            <li v-for="link in links" :key="link.id">
              <a :href="link.url" :class="{ 'is-active': activeSection === link.id }" @click="closeMenu">
                {{ link.label }}
              </a>
            </li>
          </ul>

          <a href="/#contacto" class="lf-btn lf-btn--primary lf-header__cta" @click="closeMenu">
            Cotizar proyecto
            <svg class="lf-header__cta-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </nav>
      </div>
    </div>

    <Transition name="lf-header-overlay">
      <div v-if="menuOpen" class="lf-header__overlay" aria-hidden="true" @click="closeMenu"></div>
    </Transition>
  </header>
</template>

<script>
export default {
  name: 'Header',
  data() {
    return {
      isScrolled: false,
      menuOpen: false,
      activeSection: '',
      links: [
        { id: 'servicios', url: '/#servicios', label: 'Servicios' },
        { id: 'proyectos', url: '/#proyectos', label: 'Proyectos' },
        { id: 'nosotros', url: '/#nosotros', label: 'Nosotros' },
        { id: 'contacto', url: '/#contacto', label: 'Contacto' }
      ],
      scrollObserver: null
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    document.addEventListener('keydown', this.onKey)
    this.onScroll()
    this.setupSectionObserver()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    document.removeEventListener('keydown', this.onKey)
    if (this.scrollObserver) this.scrollObserver.disconnect()
    document.body.style.overflow = ''
  },
  watch: {
    menuOpen(isOpen) {
      document.body.style.overflow = isOpen ? 'hidden' : ''
    }
  },
  methods: {
    onScroll() { this.isScrolled = window.scrollY > 20 },
    onKey(e) { if (e.key === 'Escape' && this.menuOpen) this.closeMenu() },
    toggleMenu() { this.menuOpen = !this.menuOpen },
    closeMenu() { this.menuOpen = false },
    setupSectionObserver() {
      if (!('IntersectionObserver' in window)) return
      const ids = this.links.map((l) => l.id)
      const sections = ids.map((id) => document.getElementById(id)).filter(Boolean)
      if (!sections.length) return

      this.scrollObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible.length) this.activeSection = visible[0].target.id
      }, { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] })

      sections.forEach((s) => this.scrollObserver.observe(s))
    }
  }
}
</script>

<style scoped>
.lf-header { position: fixed; top: 0; left: 0; right: 0; z-index: 900; padding: 18px 0; transition: padding 0.3s ease, background 0.3s ease, border-color 0.3s ease; border-bottom: 1px solid transparent; }
.lf-header--scrolled { padding: 12px 0; background: rgba(10, 11, 15, 0.75); backdrop-filter: saturate(180%) blur(14px); -webkit-backdrop-filter: saturate(180%) blur(14px); border-bottom-color: var(--lf-border); }
.lf-header__inner { display: flex; align-items: center; justify-content: space-between; gap: 24px; }

.lf-brand { display: flex; align-items: center; gap: 10px; font-family: var(--lf-font-display); font-weight: 600; font-size: 1.1rem; color: var(--lf-text); flex-shrink: 0; }
.lf-brand__logo { width: 36px; height: 36px; object-fit: contain; }
.lf-brand__name .lf-accent { color: var(--lf-accent); }

.lf-nav { display: flex; align-items: center; gap: 32px; }
.lf-nav ul { display: flex; gap: 28px; list-style: none; margin: 0; padding: 0; }
.lf-nav a:not(.lf-btn) { position: relative; color: var(--lf-text-mute); font-size: 14.5px; font-weight: 500; padding: 6px 0; transition: color 0.2s; }
.lf-nav a:not(.lf-btn)::after { content: ''; position: absolute; left: 0; right: 0; bottom: -2px; height: 2px; background: var(--lf-accent); border-radius: 2px; transform: scaleX(0); transform-origin: center; transition: transform 0.25s ease; }
.lf-nav a:not(.lf-btn):hover { color: var(--lf-text); }
.lf-nav a:not(.lf-btn).is-active { color: var(--lf-text); }
.lf-nav a:not(.lf-btn).is-active::after { transform: scaleX(1); }

.lf-header__cta { padding: 10px 18px; font-size: 14px; font-weight: 600; text-decoration: none; color: #001014; }
.lf-header__cta:hover { color: #001014; background: #ffffff; box-shadow: 0 0 20px rgba(0, 229, 255, 0.4); }
.lf-header__cta-arrow { transition: transform 0.25s ease; }
.lf-header__cta:hover .lf-header__cta-arrow { transform: translateX(3px); }

.lf-burger { display: none; width: 40px; height: 40px; background: transparent; border: 1px solid var(--lf-border-2); border-radius: 8px; cursor: pointer; padding: 0; flex-direction: column; align-items: center; justify-content: center; gap: 5px; transition: border-color 0.2s; z-index: 901; }
.lf-burger:hover { border-color: var(--lf-accent); }
.lf-burger:focus-visible { outline: 2px solid var(--lf-accent); outline-offset: 2px; }
.lf-burger span { display: block; width: 18px; height: 2px; background: var(--lf-text); border-radius: 2px; transition: transform 0.3s, opacity 0.3s; }
.lf-header--open .lf-burger span:first-child { transform: translateY(3.5px) rotate(45deg); }
.lf-header--open .lf-burger span:last-child { transform: translateY(-3.5px) rotate(-45deg); }

.lf-header__overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); z-index: 898; animation: lf-overlay-in 0.2s ease; }
@keyframes lf-overlay-in { from { opacity: 0; } to { opacity: 1; } }
.lf-header-overlay-enter-active, .lf-header-overlay-leave-active { transition: opacity 0.2s ease; }
.lf-header-overlay-enter-from, .lf-header-overlay-leave-to { opacity: 0; }

@media (max-width: 880px) {
  .lf-burger { display: flex; }
  .lf-nav { position: fixed; top: 0; right: 0; bottom: 0; width: min(340px, 88vw); background: var(--lf-surface); border-left: 1px solid var(--lf-border); flex-direction: column; align-items: flex-start; justify-content: flex-start; padding: 100px 32px 40px; gap: 32px; transform: translateX(100%); transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); z-index: 899; overflow-y: auto; }
  .lf-nav--open { transform: translateX(0); }
  .lf-nav ul { flex-direction: column; gap: 22px; width: 100%; }
  .lf-nav a:not(.lf-btn) { font-size: 1.05rem; padding: 4px 0; }
  .lf-nav a:not(.lf-btn)::after { bottom: -2px; transform-origin: left; }
  .lf-header__cta { width: 100%; justify-content: center; }
}
</style>